import { instancePath } from "@/lib/instance";
import { Outlet, createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { Me } from "@tern/api-client";
import { onboardingQuery } from "@/lib/onboarding";
import { meQuery, queryClient } from "@/lib/api";
import { NotFound } from "@/components/NotFound";
import { LoginPage } from "@/pages/Login";

const WorkspaceLayout = lazyRouteComponent(() => import("@/components/AppShell"), "WorkspaceLayout");
const InvitePage = lazyRouteComponent(() => import("@/pages/Invite"), "InvitePage");
const NewWorkspacePage = lazyRouteComponent(() => import("@/pages/NewWorkspace"), "NewWorkspacePage");
const InboxPage = lazyRouteComponent(() => import("@/pages/Inbox"), "InboxPage");
const MyIssuesPage = lazyRouteComponent(() => import("@/pages/MyIssues"), "MyIssuesPage");
const ProjectPlaceholderPage = lazyRouteComponent(() => import("@/pages/ProjectPlaceholder"), "ProjectPlaceholderPage");
const ProjectHomePage = lazyRouteComponent(() => import("@/pages/ProjectHome"), "ProjectHomePage");
const ProjectIssuesPage = lazyRouteComponent(() => import("@/pages/ProjectIssues"), "ProjectIssuesPage");
const ProjectSettingsPage = lazyRouteComponent(() => import("@/pages/ProjectSettings"), "ProjectSettingsPage");
const IssueDetailPage = lazyRouteComponent(() => import("@/pages/IssueDetail"), "IssueDetailPage");
const ProjectTasksPage = lazyRouteComponent(() => import("@/pages/ProjectTasks"), "ProjectTasksPage");
const TaskDetailPage = lazyRouteComponent(() => import("@/pages/TaskDetail"), "TaskDetailPage");
const ProjectMilestonesPage = lazyRouteComponent(() => import("@/pages/ProjectMilestones"), "ProjectMilestonesPage");
const ProjectDocsPage = lazyRouteComponent(() => import("@/pages/ProjectDocs"), "ProjectDocsPage");
const DocPage = lazyRouteComponent(() => import("@/pages/DocPage"), "DocPage");
const SettingsPage = lazyRouteComponent(() => import("@/pages/Settings"), "SettingsPage");
const DevOutboxPage = lazyRouteComponent(() => import("@/pages/DevOutbox"), "DevOutboxPage");

type RouterContext = { queryClient: QueryClient };

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

/** Resolve the session once; loaders below reuse the cache. */
async function requireUser(ctx: RouterContext, href: string): Promise<Me> {
  const me = await ctx.queryClient.ensureQueryData(meQuery);
  if (!me) throw redirect({ to: "/login", search: { redirect: instancePath && href.startsWith(`${instancePath}/`) ? href.slice(instancePath.length) : href } });
  return me;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): { redirect?: string; error?: string; email?: string } => ({
    ...(typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
    ...(typeof s.email === "string" ? { email: s.email } : {}),
    ...(typeof s.error === "string" ? { error: s.error } : {}),
  }),
  component: LoginPage,
});

const mcpConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mcp/authorize",
  validateSearch: (s: Record<string, unknown>): { request: string } => ({ request: typeof s.request === "string" ? s.request : "" }),
  beforeLoad: ({ context, location }) => requireUser(context, location.href),
  component: lazyRouteComponent(() => import("@/pages/Mcp"), "McpConsentPage"),
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: InvitePage,
});

const devOutboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev/outbox",
  component: DevOutboxPage,
});

const newWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/new",
  beforeLoad: ({ context, location }) => requireUser(context, location.href),
  component: NewWorkspacePage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async ({ context, location }) => {
    const me = await requireUser(context, location.href);
    if (!instancePath && me.team_onboarding) {
      const setup = await context.queryClient.fetchQuery(onboardingQuery);
      if (setup.profile.welcome_completed && setup.teams.length) {
        const team = setup.teams.find(team => team.id === setup.profile.pending_team) ?? setup.teams[0]!;
        throw redirect({ href: `/t/${team.slug}`, reloadDocument: true });
      }
      throw redirect({ to: "/new" });
    }
    const last = safeGet(`tern.${instancePath}.lastWorkspace`);
    const target = me.workspaces.find((w) => w.slug === last) ?? me.workspaces[0];
    throw redirect(target ? { to: "/$ws/inbox", params: { ws: target.slug } } : { to: "/new" });
  },
});

const databaseGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guides/company-database",
  component: lazyRouteComponent(() => import("@/pages/CompanyDatabaseGuide"), "CompanyDatabaseGuidePage"),
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$ws",
  beforeLoad: async ({ context, location, params }) => {
    const me = await requireUser(context, location.href);
    const workspace = me.workspaces.find((w) => w.slug === params.ws);
    if (!workspace) throw redirect({ to: "/" });
    safeSet(`tern.${instancePath}.lastWorkspace`, workspace.slug);
    return { me, workspace };
  },
  component: WorkspaceLayout,
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$ws/inbox", params: { ws: params.ws } });
  },
});

const inboxRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/inbox",
  validateSearch: (s: Record<string, unknown>): { filter?: "unread"; n?: string } => ({
    ...(s.filter === "unread" ? { filter: "unread" as const } : {}),
    ...(typeof s.n === "string" ? { n: s.n } : {}),
  }),
  component: InboxPage,
});

const myIssuesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/my-issues",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$ws/my-issues/$tab", params: { ws: params.ws, tab: "assigned" } });
  },
});

const myIssuesTabRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/my-issues/$tab",
  component: MyIssuesPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings",
  component: SettingsPage,
});

const settingsSectionRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings/$section",
  component: SettingsPage,
});

const projectRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key",
  component: ProjectHomePage,
});

const projectIssuesIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/issues",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$ws/project/$key/issues/$tab", params: { ws: params.ws, key: params.key, tab: "all" } });
  },
});

const projectIssuesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/issues/$tab",
  component: ProjectIssuesPage,
});

const projectSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/settings",
  component: ProjectSettingsPage,
});

const projectSettingsSectionRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/settings/$section",
  component: ProjectSettingsPage,
});

const projectTasksIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/tasks",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$ws/project/$key/tasks/$tab", params: { ws: params.ws, key: params.key, tab: "all" } });
  },
});

const projectTasksRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/tasks/$tab",
  component: ProjectTasksPage,
});

const projectMilestonesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/milestones",
  component: ProjectMilestonesPage,
});

const projectDocsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/docs",
  component: ProjectDocsPage,
});

const docRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/doc/$id",
  validateSearch: (s: Record<string, unknown>): { link?: string; n?: string } => ({
    ...(typeof s.link === "string" ? { link: s.link } : {}),
    ...(typeof s.n === "string" ? { n: s.n } : {}),
  }),
  component: DocPage,
});

const taskRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/task/$id",
  component: TaskDetailPage,
});

/** docs — placeholder until Phase 4. */
const projectSectionRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/project/$key/$section",
  component: ProjectPlaceholderPage,
});

const issueRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/issue/$identifier",
  component: IssueDetailPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  devOutboxRoute,
  newWorkspaceRoute,
  databaseGuideRoute,
  mcpConsentRoute,
  indexRoute,
  workspaceRoute.addChildren([
    workspaceIndexRoute,
    inboxRoute,
    myIssuesRoute,
    myIssuesTabRoute,
    settingsRoute,
    settingsSectionRoute,
    projectRoute,
    projectIssuesIndexRoute,
    projectIssuesRoute,
    projectSettingsRoute,
    projectSettingsSectionRoute,
    projectTasksIndexRoute,
    projectTasksRoute,
    projectMilestonesRoute,
    projectDocsRoute,
    docRoute,
    projectSectionRoute,
    issueRoute,
    taskRoute,
  ]),
]);

export const router = createRouter({
  basepath: instancePath || "/",
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
